import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "@components/Loader";
import {
  useTimeSlotsAdminQuery,
  useCreateTimeSlotMutation,
  useUpdateTimeSlotMutation,
  useDeleteTimeSlotMutation
} from "@queries/time_slots";
import "@styles/opencart-admin.css";

const TIME_OPTIONS = [
  "00:00",
  "12:30",
  "01:00",
  "01:30",
  "02:00",
  "02:30",
  "03:00",
  "03:30",
  "04:00",
  "04:30",
  "05:00",
  "05:30",
  "06:00",
  "06:30",
  "07:00",
  "07:30",
  "08:00",
  "08:30",
  "09:00",
  "09:30",
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "12:00",
  "12:30",
  "13:00",
  "13:30",
  "14:00",
  "14:30",
  "15:00",
  "15:30",
  "16:00",
  "16:30",
  "17:00",
  "17:30",
  "18:00",
  "18:30",
  "19:00",
  "19:30",
  "20:00",
  "20:30",
  "21:00",
  "21:30",
  "22:00",
  "22:30",
  "23:00",
  "23:30"
];

const defaultForm = {
  start_time: "",
  end_time: "",
  type: "pickup",
  status: 1,
  capacity: 0
};

export default function TimeSlotsPage() {
  const [filters, setFilters] = React.useState({
    type: "",
    status: ""
  });
  const [page, setPage] = React.useState(1);
  const pageSize = 50;

  const [editingId, setEditingId] = React.useState(null);
  const [form, setForm] = React.useState(defaultForm);

  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, isError, error } =
    useTimeSlotsAdminQuery(
      {
        type: filters.type || undefined,
        status: filters.status || undefined
      },
      page,
      pageSize
    );

  const createMutation = useCreateTimeSlotMutation();
  const updateMutation = useUpdateTimeSlotMutation();
  const deleteMutation = useDeleteTimeSlotMutation();

  const rows = data?.data || [];
  const total = data?.meta?.total ?? rows.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  function resetForm() {
    setEditingId(null);
    setForm(defaultForm);
  }

  function openEdit(row) {
    const id = row.id ?? row.time_slot_id ?? row.slot_id;

    let start = row.start_time || "";
    let end = row.end_time || "";

    // If API only returns "presentation" like "12:00 AM - 01:30 AM"
    if ((!start || !end) && row.presentation) {
      const parts = String(row.presentation).split("-");
      if (parts.length === 2) {
        start = parts[0].trim();
        end = parts[1].trim();
      }
    }

    setEditingId(id);
    setForm({
      start_time: start,
      end_time: end,
      type: row.type || "pickup",
      status: typeof row.status === "number" ? row.status : row.status ? 1 : 0,
      capacity: row.capacity ?? 0
    });
  }

  async function handleSave(e) {
    e.preventDefault();

    const payload = {
      start_time: form.start_time,
      end_time: form.end_time,
      type: form.type,
      status: Number(form.status),
      capacity: Number(form.capacity) || 0
    };

    if (!payload.start_time || !payload.end_time) {
      alert("Start time and end time are required.");
      return;
    }

    if (editingId) {
      await updateMutation.mutateAsync({ id: editingId, payload });
    } else {
      await createMutation.mutateAsync(payload);
    }

    resetForm();
    queryClient.invalidateQueries({ queryKey: ["time-slots-admin"] });
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this time slot?")) return;
    await deleteMutation.mutateAsync(id);
    queryClient.invalidateQueries({ queryKey: ["time-slots-admin"] });
  }

  function handleFilterChange(field, value) {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  }

  function formatPresentation(row) {
    if (row.presentation) return row.presentation;
    if (row.start_time && row.end_time) {
      return `${row.start_time} - ${row.end_time}`;
    }
    return "";
  }

  function formatStatus(value) {
    const v = typeof value === "number" ? value : value ? 1 : 0;
    return v === 1 ? "Enabled" : "Disabled";
  }

  return (
    <div className="oc-content">
      {(isLoading || isFetching) && <Loader />}

      <div className="oc-header">
        <h1>Time Slots</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Time Slots</span>
        </nav>
      </div>

      <div className="oc-card">
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>{editingId ? "Edit Time Slot" : "Add Time Slot"}</strong>
          </div>
          <div>
            <button className="oc-btn ghost" onClick={resetForm}>
              New
            </button>
          </div>
        </div>

        <form onSubmit={handleSave}>
          <div className="form-row">
            <div className="form-group">
              <label>Type</label>
              <select
                className="oc-input"
                value={form.type}
                onChange={(e) =>
                  setForm((f) => ({ ...f, type: e.target.value }))
                }
              >
                <option value="dropoff">dropoff</option>
                <option value="pickup">pickup</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Start Time</label>
              <select
                className="oc-input"
                value={form.start_time}
                onChange={(e) =>
                  setForm((f) => ({ ...f, start_time: e.target.value }))
                }
                required
              >
                <option value="">Select</option>
                {TIME_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>End Time</label>
              <select
                className="oc-input"
                value={form.end_time}
                onChange={(e) =>
                  setForm((f) => ({ ...f, end_time: e.target.value }))
                }
                required
              >
                <option value="">Select</option>
                {TIME_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select
                className="oc-input"
                value={form.status}
                onChange={(e) =>
                  setForm((f) => ({ ...f, status: e.target.value }))
                }
              >
                <option value={1}>Enabled</option>
                <option value={0}>Disabled</option>
              </select>
            </div>
            <div className="form-group">
              <label>Capacity</label>
              <input
                type="number"
                className="oc-input"
                value={form.capacity}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    capacity: e.target.value
                  }))
                }
                min={0}
              />
            </div>
          </div>

          <div
            style={{
              marginTop: 12,
              display: "flex",
              justifyContent: "flex-end",
              gap: 8
            }}
          >
            <button
              type="button"
              className="oc-btn ghost"
              onClick={resetForm}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="oc-btn primary"
              disabled={
                createMutation.isLoading || updateMutation.isLoading
              }
            >
              Save
            </button>
          </div>
        </form>
      </div>

      <div className="oc-card" style={{ marginTop: 16 }}>
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>Time Slots</strong>
          </div>
        </div>

        <div>
          <div>
            {isError && (
              <div style={{ color: "#c00", marginBottom: 8 }}>
                Error: {error?.message || "Unknown error"}
              </div>
            )}

            <table className="oc-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th style={{ width: 100 }}>Type</th>
                  <th style={{ width: 100 }}>Status</th>
                  <th style={{ width: 100 }}>Capacity</th>
                  <th style={{ width: 160 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", padding: 24 }}>
                      No time slots
                    </td>
                  </tr>
                )}
                {rows.map((row) => {
                  const id = row.id ?? row.time_slot_id ?? row.slot_id;
                  return (
                    <tr key={id}>
                      <td>{formatPresentation(row)}</td>
                      <td>{row.type}</td>
                      <td>{formatStatus(row.status)}</td>
                      <td>{row.capacity}</td>
                      <td>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            className="oc-btn blue"
                            onClick={() => openEdit(row)}
                          >
                            edit
                          </button>
                          <button
                            className="oc-btn danger"
                            onClick={() => handleDelete(id)}
                          >
                            delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="oc-card" style={{ display: "none" }}>
            <div className="title">Filter</div>
            <div className="form-group">
              <label>Type</label>
              <select
                className="oc-input"
                value={filters.type}
                onChange={(e) =>
                  handleFilterChange("type", e.target.value)
                }
              >
                <option value="">All</option>
                <option value="dropoff">dropoff</option>
                <option value="pickup">pickup</option>
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                className="oc-input"
                value={filters.status}
                onChange={(e) =>
                  handleFilterChange("status", e.target.value)
                }
              >
                <option value="">All</option>
                <option value="1">Enabled</option>
                <option value="0">Disabled</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div
        className="oc-card"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 16
        }}
      >
        <div>
          Showing page <strong>{page}</strong> of <strong>{pages}</strong> (
          {total} total)
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="oc-btn ghost"
            disabled={page <= 1}
            onClick={() => setPage(1)}
          >
            « First
          </button>
          <button
            className="oc-btn ghost"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ‹ Prev
          </button>
          <button
            className="oc-btn ghost"
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next ›
          </button>
          <button
            className="oc-btn ghost"
            disabled={page >= pages}
            onClick={() => setPage(pages)}
          >
            Last »
          </button>
        </div>
      </div>
    </div>
  );
}

